import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { HiChevronRight, HiCheck } from 'react-icons/hi';
import { FaWhatsapp, FaRobot } from 'react-icons/fa';
import { HiLocationMarker, HiCog } from 'react-icons/hi';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { SERVICES } from '../utils/constants';

const iconComponents = {
  WhatsApp: FaWhatsapp,
  Robot: FaRobot,
  Target: HiLocationMarker,
  Cog: HiCog,
};

const ServiceDetailCard = ({ service, index }) => {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.2 });
  const Icon = iconComponents[service.icon];

  return (
    <motion.div
      id={service.id}
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isVisible ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.1, ease: 'easeOut' }}
      style={{ scrollMarginTop: '6rem' }}
    >
      <div style={{
        background: '#FFFFFF',
        border: '1px solid var(--color-border)',
        borderRadius: '16px',
        overflow: 'hidden',
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: '0',
        }} className="service-detail-grid">
          {/* Main content */}
          <div style={{ padding: 'clamp(1.5rem, 3vw, 2.5rem)' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '0.75rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <Icon size={24} color="#1A1A1A" />
                <h2 style={{
                  fontSize: 'clamp(1.25rem, 2.5vw, 1.75rem)',
                  fontWeight: 700,
                  color: '#1A1A1A',
                  fontFamily: '"DM Sans", sans-serif',
                }}>
                  {service.title}
                </h2>
              </div>
              {service.popular && (
                <span style={{
                  fontSize: '0.7rem', fontWeight: 600, padding: '4px 12px',
                  backgroundColor: '#1A1A1A', color: '#FFFFFF', borderRadius: '999px',
                  fontFamily: '"DM Sans", sans-serif',
                }}>
                  {service.tag}
                </span>
              )}
            </div>

            <p style={{
              fontSize: '1rem', lineHeight: 1.7, color: '#666666',
              marginBottom: '1.5rem', fontFamily: '"DM Sans", sans-serif',
            }}>
              {service.description}
            </p>

            {/* Features grid */}
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr', gap: '0.5rem',
              marginBottom: '1.5rem',
            }} className="features-grid">
              {service.features.map((feature, idx) => (
                <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                  <HiCheck size={16} color="#1A1A1A" style={{ marginTop: '3px', flexShrink: 0 }} />
                  <span style={{ fontSize: '0.9rem', color: '#333333', fontFamily: '"DM Sans", sans-serif' }}>
                    {feature}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Pricing sidebar */}
          <div style={{
            padding: 'clamp(1.5rem, 3vw, 2.5rem)',
            backgroundColor: '#FAF7F2',
            borderTop: '1px solid var(--color-border)',
          }} className="service-pricing">
            <h3 style={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#999999', marginBottom: '1.25rem', fontFamily: '"DM Sans", sans-serif' }}>
              Investment
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span style={{ fontSize: '0.875rem', color: '#666666', fontFamily: '"DM Sans", sans-serif' }}>Setup Fee</span>
                <span style={{ fontSize: '1.25rem', fontWeight: 700, color: '#1A1A1A', fontFamily: '"DM Sans", sans-serif' }}>{service.pricing.setup}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span style={{ fontSize: '0.875rem', color: '#666666', fontFamily: '"DM Sans", sans-serif' }}>Monthly</span>
                <span style={{ fontSize: '1.25rem', fontWeight: 700, color: '#1A1A1A', fontFamily: '"DM Sans", sans-serif' }}>{service.pricing.monthly}</span>
              </div>
            </div>

            <p style={{ fontSize: '0.8rem', color: '#999999', lineHeight: 1.6, marginBottom: '1.5rem', fontFamily: '"DM Sans", sans-serif' }}>
              One-time setup includes full configuration, testing, and team training. Monthly fee covers hosting, monitoring, and support.
            </p>

            <Link
              to="/contact"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                padding: '12px', backgroundColor: '#1A1A1A', color: '#FFFFFF',
                borderRadius: '999px', fontSize: '0.9rem', fontWeight: 600,
                textDecoration: 'none', fontFamily: '"DM Sans", sans-serif',
                width: '100%',
              }}
            >
              Get Started <HiChevronRight size={16} />
            </Link>

            {/* Trust */}
            <div style={{ marginTop: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
              {['5-day setup guarantee', '30-day money back guarantee', 'Ongoing support included'].map((t) => (
                <div key={t} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <HiCheck size={14} color="#666666" />
                  <span style={{ fontSize: '0.8rem', color: '#666666', fontFamily: '"DM Sans", sans-serif' }}>{t}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const Services = () => {
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation({ threshold: 0.3 });

  return (
    <>
      <Helmet>
        <title>Services | AI Automation Solutions | Rishant Technologies</title>
        <meta name="description" content="Explore our AI automation services: WhatsApp bots, customer support automation, lead generation systems, and business process automation." />
        <link rel="canonical" href="https://rishant.tech/services" />
      </Helmet>

      <main style={{ paddingTop: '80px' }}>
        {/* Header */}
        <section ref={headerRef} style={{ backgroundColor: '#F5F0E8', padding: 'clamp(4rem, 8vw, 7rem) 1.5rem' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={headerVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, ease: 'easeOut' }}
              style={{ maxWidth: '720px' }}
            >
              <h1 style={{
                fontSize: 'clamp(2.5rem, 5vw, 4rem)',
                fontWeight: 700,
                lineHeight: 1.08,
                letterSpacing: '-0.03em',
                color: '#1A1A1A',
                fontFamily: '"DM Sans", sans-serif',
                marginBottom: '1.5rem',
              }}>
                AI automation{' '}
                <span style={{ textDecoration: 'underline', textDecorationThickness: '3px', textUnderlineOffset: '6px' }}>
                  solutions
                </span>
                {' '}built for your business
              </h1>
              <p style={{
                fontSize: '1.15rem',
                lineHeight: 1.7,
                color: '#666666',
                marginBottom: '2rem',
                fontFamily: '"DM Sans", sans-serif',
              }}>
                Choose the automation that fits your needs. All services include setup, configuration, training, and ongoing support.
              </p>
              <Link
                to="/contact"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '8px',
                  padding: '14px 28px', backgroundColor: '#1A1A1A', color: '#FFFFFF',
                  borderRadius: '999px', fontSize: '0.95rem', fontWeight: 600,
                  textDecoration: 'none', fontFamily: '"DM Sans", sans-serif',
                }}
              >
                Book Free Consultation <HiChevronRight size={18} />
              </Link>
            </motion.div>
          </div>
        </section>

        {/* Service Cards */}
        <section style={{ backgroundColor: '#FAF7F2', padding: 'clamp(4rem, 8vw, 7rem) 1.5rem', borderTop: '1px solid var(--color-border)' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'clamp(2rem, 4vw, 3rem)' }}>
              {SERVICES.map((service, index) => (
                <ServiceDetailCard key={service.id} service={service} index={index} />
              ))}
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section style={{ backgroundColor: '#111111', padding: 'clamp(4rem, 8vw, 7rem) 1.5rem' }}>
          <div style={{ maxWidth: '700px', margin: '0 auto', textAlign: 'center' }}>
            <h2 style={{
              fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)',
              fontWeight: 700,
              letterSpacing: '-0.02em',
              color: '#FFFFFF',
              marginBottom: '1rem',
              fontFamily: '"DM Sans", sans-serif',
            }}>
              Not sure which service you need?
            </h2>
            <p style={{
              fontSize: '1rem', lineHeight: 1.65, color: 'rgba(255,255,255,0.5)',
              marginBottom: '2rem', fontFamily: '"DM Sans", sans-serif',
            }}>
              Book a free 20-minute consultation and we'll help you identify the best automation opportunities.
            </p>
            <Link
              to="/contact"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '8px',
                padding: '14px 28px', backgroundColor: '#FFFFFF', color: '#111111',
                borderRadius: '999px', fontSize: '0.95rem', fontWeight: 600,
                textDecoration: 'none', fontFamily: '"DM Sans", sans-serif',
              }}
            >
              Book Free Consultation <HiChevronRight size={18} />
            </Link>
          </div>
        </section>
      </main>

      <style>{`
        @media (min-width: 1024px) {
          .service-detail-grid { grid-template-columns: 1.5fr 1fr !important; }
          .service-pricing { border-top: none !important; border-left: 1px solid var(--color-border) !important; }
        }
        @media (min-width: 640px) {
          .features-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
      `}</style>
    </>
  );
};

export default Services;
